// Resolution of the position-size display grid: values are quantized to the
// nearest multiple of 1/GRID_DENOMINATOR, then reduced to their simplest
// fraction. 16 is the minimum resolution -- a "sixteenth" of standard size is
// the smallest size traders distinguish here.
const GRID_DENOMINATOR = 16;

// Exception to the 1/16 floor: below one sixteenth, a two-way choice between
// "0" and "1/16" is too coarse for a genuinely tiny starter/scale-in size, so
// that one pocket also checks the halfway rung at 1/32.
const SUB_UNIT_FRACTION = 1 / (GRID_DENOMINATOR * 2); // 1/32
const SUB_UNIT_LOWER_MIDPOINT = SUB_UNIT_FRACTION / 2; // midpoint between 0 and 1/32 -> 1/64
const SUB_UNIT_UPPER_MIDPOINT = (SUB_UNIT_FRACTION + 1 / GRID_DENOMINATOR) / 2; // midpoint between 1/32 and 1/16 -> 3/64

const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));

// Maps a raw position size (current_value / standard_size) onto a uniform
// 1/16-resolution grid and reduces it to its simplest fraction, e.g.
// 0.3125 -> "5/16", 0.75 -> "3/4" (not "1" -- see below). Values above 1 use
// the same grid for the fractional remainder, producing a mixed number, e.g.
// 2.3 -> "2 5/16". Below 1/16, one extra rung at 1/32 is checked (see
// SUB_UNIT_* above) so tiny sizes near 1/32 aren't rounded all the way down
// to "0" or all the way up to a full "1/16".
//
// A uniform grid (rather than a halving ladder of 1, 1/2, 1/4, 1/8, ...)
// avoids two distortions the old ladder had: it can represent in-between
// sizes like 5/16 instead of always snapping to the nearest power-of-2, and
// it doesn't have a "hole" at 3/4 (the old <=1 ladder had no 3/4 entry, so
// anything from 0.75 up to just under 1 rounded all the way up to "1").
export const formatPositionSizeFraction = (value) => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return null;
  }
  if (value <= 0) {
    return '0';
  }

  const totalGridUnits = Math.round(value * GRID_DENOMINATOR);
  const whole = Math.floor(totalGridUnits / GRID_DENOMINATOR);
  const remainder = totalGridUnits % GRID_DENOMINATOR;

  // Only the bottom pocket (below ~3/32, i.e. closest to "0" or "1/16" on
  // the coarse grid) needs the finer 1/32 check -- everywhere else the
  // regular 1/16 grid already applies.
  if (whole === 0 && remainder <= 1) {
    if (value < SUB_UNIT_LOWER_MIDPOINT) {
      return '0';
    }
    if (value < SUB_UNIT_UPPER_MIDPOINT) {
      return '1/32';
    }
    return '1/16';
  }

  if (remainder === 0) {
    return `${whole}`;
  }

  const divisor = gcd(remainder, GRID_DENOMINATOR);
  const fractionLabel = `${remainder / divisor}/${GRID_DENOMINATOR / divisor}`;

  return whole === 0 ? fractionLabel : `${whole} ${fractionLabel}`;
};
